import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { collection, getDocs } from "firebase/firestore";
import { db, functions } from "../../config/firebaseConfig";
import { ADMIN_UIDS } from "../Auth/authSlice";

// Current month as "YYYY-MM" (HTML month-input value)
function currentMonthInput() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

// "DD-MM-YYYY" doc-id date → "DD/MM/YYYY" for display
function displayDate(ddmmyyyy) {
  return ddmmyyyy.replaceAll("-", "/");
}

function parseFrench(str) {
  return parseFloat(
    String(str ?? "")
      .replace(/[   ]/g, "")
      .replace(",", "."),
  );
}

function fmtNum(val) {
  const n = parseFrench(val);
  if (isNaN(n)) return val ?? "—";
  return n.toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function fmtDateTime(ms) {
  return new Date(ms).toLocaleString("fr-FR", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

// Number of calendar days in a "YYYY-MM" month input value.
function daysInMonth(monthInput) {
  const [y, m] = monthInput.split("-").map(Number);
  return new Date(y, m, 0).getDate();
}

// v.matched is undefined until a bank statement has been loaded for this
// row's month/devise — distinguish "not checked yet" from "checked, no
// matching amount found in the statement".
function versStatus(v) {
  if (v.matched === undefined)
    return { label: "⏳ Relevé non chargé", color: "#64748b" };
  if (v.matched) return { label: "✅ Conforme", color: "#22c55e" };
  return { label: "❌ Introuvable", color: "#ef4444" };
}

// Rapprochement fait par Gemini (validateVersementsGemini.js)
function geminiBankStatus(v) {
  switch (v.geminiBank?.status) {
    case "conforme":
      return { label: "✅ Conforme", color: "#22c55e" };
    case "probable":
      return { label: "🟡 Probable", color: "#eab308" };
    case "ecart":
      return { label: "⚠️ Écart de montant", color: "#f59e0b" };
    case "introuvable":
      return { label: "❌ Introuvable", color: "#ef4444" };
    default:
      return { label: "⏳ Relevé non analysé", color: "#64748b" };
  }
}

const EMPTY_DAY_STATUS = { label: "🚫 Aucune déclaration", color: "#ef4444" };
const ORPHAN_STATUS = { label: "🔎 Banque sans versement", color: "#a855f7" };

// "DD/MM/YYYY" (as found in a bank export) → "DD-MM-YYYY" (dateVers format)
function slashToDash(ddmmyyyy) {
  return ddmmyyyy.replaceAll("/", "-");
}

// One row per calendar day of the selected month (not just days that
// have a versement) so a day the pompiste never declared shows up as a
// visible gap instead of silently disappearing from the table. Dates can
// repeat: a day with a declared versement AND an unclaimed bank deposit
// (or several of either) gets one row per item, so nothing is collapsed
// away — the point is to make every discrepancy visible for investigation.
// Shared by both tabs (rule-based and Gemini reconciliation).
function buildTableRows(versements, orphanBankRows, monthInput) {
  const [yyyyM, mmM] = monthInput.split("-");
  const versByDate = new Map();
  for (const v of versements) {
    if (!versByDate.has(v.dateVers)) versByDate.set(v.dateVers, []);
    versByDate.get(v.dateVers).push(v);
  }
  const orphansByDate = new Map();
  for (const o of orphanBankRows) {
    if (!o.date) continue;
    const key = slashToDash(o.date);
    if (!orphansByDate.has(key)) orphansByDate.set(key, []);
    orphansByDate.get(key).push(o);
  }
  const monthDateKeys = new Set();
  for (let day = 1; day <= daysInMonth(monthInput); day++) {
    monthDateKeys.add(`${String(day).padStart(2, "0")}-${mmM}-${yyyyM}`);
  }

  const tableRows = [];
  for (let day = 1; day <= daysInMonth(monthInput); day++) {
    const dateVers = `${String(day).padStart(2, "0")}-${mmM}-${yyyyM}`;
    const items = versByDate.get(dateVers) || [];
    const orphans = orphansByDate.get(dateVers) || [];
    if (items.length === 0 && orphans.length === 0) {
      tableRows.push({ type: "empty", key: dateVers, dateVers });
      continue;
    }
    items.forEach((v, i) =>
      tableRows.push({ type: "vers", key: `${dateVers}_v${i}`, dateVers, v }),
    );
    orphans.forEach((o, i) =>
      tableRows.push({ type: "orphan", key: `${dateVers}_o${i}`, dateVers, o }),
    );
  }

  // Unmatched bank deposits whose value date lags into an adjacent month
  // (routine — the bank often clears a deposit a few days after it
  // happened) don't have a calendar-day slot in the month above. Show them
  // as trailing rows instead of silently dropping them — that lag is
  // exactly the kind of gap this table exists to surface.
  const outsideMonthOrphanDates = [...orphansByDate.keys()]
    .filter((key) => !monthDateKeys.has(key))
    .sort((a, b) => {
      const [da, ma, ya] = a.split("-").map(Number);
      const [db_, mb, yb] = b.split("-").map(Number);
      return new Date(ya, ma - 1, da) - new Date(yb, mb - 1, db_);
    });
  for (const dateVers of outsideMonthOrphanDates) {
    orphansByDate.get(dateVers).forEach((o, i) =>
      tableRows.push({ type: "orphan", key: `out_${dateVers}_o${i}`, dateVers, o }),
    );
  }

  return tableRows;
}

export default function AdminValidation() {
  const navigate = useNavigate();
  const [stations, setStations] = useState([]);
  const [selectedUid, setSelectedUid] = useState("");
  const [monthInput, setMonthInput] = useState(currentMonthInput());
  const [deviseInput, setDeviseInput] = useState("CDF");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [reconciling, setReconciling] = useState(false);
  const [reconcileError, setReconcileError] = useState(null);
  const [orphanBankRows, setOrphanBankRows] = useState([]);
  const [photoModal, setPhotoModal] = useState(null); // { loading, url, error, item, dateVers }
  const [tab, setTab] = useState("banque"); // "banque" | "gemini"
  const [geminiReconciling, setGeminiReconciling] = useState(false);
  const [geminiError, setGeminiError] = useState(null);
  const [geminiOrphans, setGeminiOrphans] = useState([]);

  useEffect(() => {
    async function loadStations() {
      const snap = await getDocs(collection(db, "users"));
      const list = [];
      const seen = new Set();
      snap.forEach((doc) => {
        if (ADMIN_UIDS.includes(doc.id)) return;
        if (doc.id.startsWith("test_")) return;
        const d = doc.data();
        const key = String(d.station || "").toUpperCase();
        if (!key || seen.has(key)) return;
        seen.add(key);
        list.push({ uid: doc.id, station: d.station });
      });
      list.sort((a, b) => (a.station || "").localeCompare(b.station || ""));
      setStations(list);
      const barumbu = list.find((s) => (s.station || "").toUpperCase() === "BARUMBU");
      if (barumbu) setSelectedUid(barumbu.uid);
      else if (list.length) setSelectedUid(list[0].uid);
    }
    loadStations();
  }, []);

  const handleLoad = async () => {
    if (!selectedUid) return;
    setLoading(true);
    setError(null);
    setData(null);
    setReconcileError(null);
    setGeminiError(null);
    setOrphanBankRows([]);
    setGeminiOrphans([]);
    try {
      const fn = httpsCallable(functions, "getValidationData");
      const res = await fn({
        uid: selectedUid,
        month: monthInput,
        devise: deviseInput,
      });
      // `matched` is derived from a bank line persisted by a previous
      // reconcileBankStatement run, if any — so a match survives reload
      // without re-uploading the statement.
      setData({
        ...res.data,
        versements: (res.data.versements || []).map((v) => ({
          ...v,
          matched: v.bankMontant != null ? true : undefined,
        })),
      });
    } catch (e) {
      setError(e.message || "Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  // Keep the table/report in sync with the filters — reload as soon as
  // station, month or devise changes instead of leaving stale data from
  // the previous selection on screen until "Charger" is clicked again.
  useEffect(() => {
    if (!selectedUid) return;
    handleLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUid, monthInput, deviseInput]);

  const readFileAsBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(",")[1] || "");
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  // Un seul chargement du relevé alimente les deux onglets : rapprochement
  // classique (CSV uniquement) et rapprochement Gemini (CSV ou PDF), en
  // parallèle — chacun gère son propre chargement et sa propre erreur.
  const handleStatementUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file later
    if (!file || !selectedUid) return;

    const ext = file.name.split(".").pop()?.toLowerCase();
    if (!["csv", "pdf"].includes(ext)) {
      const msg = "Format non supporté — utilisez un fichier .csv ou .pdf";
      setReconcileError(msg);
      setGeminiError(msg);
      return;
    }
    setGeminiError(null);
    setReconcileError(
      ext === "pdf"
        ? "Relevé PDF : seul le rapprochement Gemini est disponible (onglet 🤖)."
        : null,
    );

    const fileBase64 = await readFileAsBase64(file);
    await Promise.all([
      ext === "csv" && reconcileStatement(fileBase64),
      reconcileWithGemini(fileBase64, ext === "pdf" ? "application/pdf" : "text/csv"),
    ]);
  };

  const reconcileStatement = async (fileBase64) => {
    setReconciling(true);
    try {
      const fn = httpsCallable(functions, "reconcileBankStatement");
      const res = await fn({
        uid: selectedUid,
        month: monthInput,
        devise: deviseInput,
        fileBase64,
      });
      const { results, unmatchedBankTransactions } = res.data;
      setOrphanBankRows(unmatchedBankTransactions || []);

      // Merge matched/verified status back into the displayed table.
      setData((prev) => {
        const byKey = new Map(
          results.map((r) => [`${r.dateVers}_${r.item}`, r]),
        );
        const merged = (prev?.versements || []).map((v) => {
          const r = byKey.get(`${v.dateVers}_${v.item}`);
          return r
            ? {
                ...v,
                verified: r.verified,
                matched: r.matched,
                bankMontant: r.bankMontant,
                bankDate: r.bankDate,
                bankLibelle: r.bankLibelle,
                bankMatchedAt: r.bankMatchedAt,
              }
            : v;
        });
        return { ...prev, versements: merged };
      });
    } catch (err) {
      setReconcileError(err.message || "Erreur lors du rapprochement");
    } finally {
      setReconciling(false);
    }
  };

  // Même relevé, rapprochement fait par Gemini. Résultat mémorisé dans
  // versement.geminiBank.
  const reconcileWithGemini = async (fileBase64, mimeType) => {
    setGeminiReconciling(true);
    try {
      const fn = httpsCallable(functions, "validateVersementsGemini", {
        timeout: 300000,
      });
      const res = await fn({
        uid: selectedUid,
        month: monthInput,
        devise: deviseInput,
        fileBase64,
        mimeType,
      });
      const { results, unmatchedBankTransactions } = res.data;
      setGeminiOrphans(unmatchedBankTransactions || []);
      const byKey = new Map(
        results.map((r) => [`${r.dateVers}_${r.item}`, r.geminiBank]),
      );
      setData((prev) => ({
        ...prev,
        versements: prev.versements.map((v) => {
          const g = byKey.get(`${v.dateVers}_${v.item}`);
          return g ? { ...v, geminiBank: g } : v;
        }),
      }));
    } catch (err) {
      setGeminiError(err.message || "Erreur lors du rapprochement Gemini");
    } finally {
      setGeminiReconciling(false);
    }
  };

  // Manually flips the "Déjà vérifié" checkbox and persists it (see
  // setVersementVerified.js) — updates local state optimistically and
  // reverts if the call fails.
  const handleToggleVerified = async (v) => {
    const next = !v.verified;
    setData((prev) => ({
      ...prev,
      versements: prev.versements.map((row) =>
        row.item === v.item && row.dateVers === v.dateVers
          ? { ...row, verified: next, banque: next ? "Confirmé manuellement (admin)" : null }
          : row,
      ),
    }));
    try {
      const fn = httpsCallable(functions, "setVersementVerified");
      await fn({ uid: selectedUid, item: v.item, dateVers: v.dateVers, verified: next });
    } catch (err) {
      setData((prev) => ({
        ...prev,
        versements: prev.versements.map((row) =>
          row.item === v.item && row.dateVers === v.dateVers
            ? { ...row, verified: v.verified, banque: v.banque }
            : row,
        ),
      }));
      setError(err.message || "Erreur lors de la validation");
    }
  };

  const handleRowClick = async (v) => {
    setPhotoModal({ loading: true, url: null, error: null, item: v.item, dateVers: v.dateVers });
    try {
      const fn = httpsCallable(functions, "getBvPhotoUrl");
      const res = await fn({ uid: selectedUid, item: v.item, dateVers: v.dateVers });
      setPhotoModal({
        loading: false,
        url: res.data.url,
        error: res.data.url ? null : "Aucune photo BV pour cette date.",
        item: v.item,
        dateVers: v.dateVers,
      });
    } catch (err) {
      setPhotoModal({
        loading: false,
        url: null,
        error: err.message || "Erreur lors du chargement de la photo",
        item: v.item,
        dateVers: v.dateVers,
      });
    }
  };

  const versements = data?.versements || [];

  const totalMontantPompiste = versements.reduce(
    (sum, v) => sum + Number(v.montant || 0),
    0,
  );
  const totalMontantBanque = versements.reduce(
    (sum, v) => sum + (v.bankMontant != null ? Number(v.bankMontant) : 0),
    0,
  );

  // Derived from whatever's currently loaded — reflects persisted bank
  // matches from a previous reconciliation too, not just one done just now.
  const bankMatchedTimes = versements
    .map((v) => v.bankMatchedAt)
    .filter((ms) => ms != null);
  const report = {
    total: versements.length,
    confirmed: versements.filter((v) => v.matched).length,
    notFound: versements.filter((v) => v.matched === false).length,
    pending: versements.filter((v) => v.matched === undefined).length,
    sumPompiste: totalMontantPompiste,
    sumBanque: totalMontantBanque,
    difference: totalMontantPompiste - totalMontantBanque,
    lastUploadedAt: bankMatchedTimes.length ? Math.max(...bankMatchedTimes) : null,
    unmatchedBankCount: orphanBankRows.length,
    unmatchedBankSum: orphanBankRows.reduce((sum, o) => sum + Number(o.montant || 0), 0),
  };

  const geminiCheckedTimes = versements
    .map((v) => v.geminiBank?.checkedAt)
    .filter((ms) => ms != null);
  const geminiReport = {
    total: versements.length,
    conforme: versements.filter((v) => v.geminiBank?.status === "conforme").length,
    probable: versements.filter((v) => v.geminiBank?.status === "probable").length,
    ecart: versements.filter((v) => v.geminiBank?.status === "ecart").length,
    notFound: versements.filter((v) => v.geminiBank?.status === "introuvable").length,
    pending: versements.filter((v) => !v.geminiBank).length,
    sumBanque: versements.reduce(
      (sum, v) =>
        sum + (v.geminiBank?.bankMontant != null ? Number(v.geminiBank.bankMontant) : 0),
      0,
    ),
    lastAt: geminiCheckedTimes.length ? Math.max(...geminiCheckedTimes) : null,
    unmatchedBankCount: geminiOrphans.length,
    unmatchedBankSum: geminiOrphans.reduce((sum, o) => sum + Number(o.montant || 0), 0),
  };
  geminiReport.difference = totalMontantPompiste - geminiReport.sumBanque;

  const tableRows = buildTableRows(versements, orphanBankRows, monthInput);
  const geminiTableRows = buildTableRows(versements, geminiOrphans, monthInput);

  return (
    <div style={s.root} className="admin-menu-root">
      <div style={s.card} className="admin-menu-card">
        {/* Header */}
        <div style={s.header}>
          <button style={s.backBtn} onClick={() => navigate("/menu")}>
            ← Menu
          </button>
          <div>
            <div style={s.title}>⛽ VALIDATION VERSEMENTS</div>
            <div style={s.subtitle}>Contrôle des saisies pompiste</div>
          </div>
        </div>

        {/* Onglets : relevé bancaire (CSV) / lecture Gemini des photos BV */}
        <div style={s.tabs}>
          {[
            { key: "banque", label: "🏦 Relevé bancaire" },
            { key: "gemini", label: "🤖 Rapprochement Gemini" },
          ].map((t) => (
            <button
              key={t.key}
              style={{ ...s.tab, ...(tab === t.key ? s.tabActive : {}) }}
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Selectors */}
        <div style={s.selRow} className="mobile-stack">
          <div style={s.selGroup}>
            <label style={s.label}>Station</label>
            <select
              style={s.select}
              value={selectedUid}
              onChange={(e) => setSelectedUid(e.target.value)}
            >
              {stations.map((st) => (
                <option key={st.uid} value={st.uid}>
                  {st.station}
                </option>
              ))}
            </select>
          </div>
          <div style={s.selGroup}>
            <label style={s.label}>Mois</label>
            <input
              type="month"
              style={s.dateInput}
              value={monthInput}
              onChange={(e) => setMonthInput(e.target.value)}
            />
          </div>
          <div style={s.selGroup}>
            <label style={s.label}>Devise</label>
            <select
              style={s.select}
              value={deviseInput}
              onChange={(e) => setDeviseInput(e.target.value)}
            >
              <option value="CDF">CDF</option>
              <option value="USD">USD</option>
            </select>
          </div>
          {data && (
            <div style={s.selGroup}>
              <label style={s.label}>Relevé bancaire</label>
              <label
                style={s.fileBtn}
                title="Un seul chargement : rapprochement classique + Gemini"
              >
                {reconciling || geminiReconciling ? (
                  <>
                    <span className="spinner" style={s.spinnerInline} />
                    {reconciling ? "Analyse…" : "Gemini analyse…"}
                  </>
                ) : (
                  "Charger CSV / PDF"
                )}
                <input
                  type="file"
                  accept=".csv,.pdf"
                  onChange={handleStatementUpload}
                  disabled={reconciling || geminiReconciling}
                  style={{ display: "none" }}
                />
              </label>
            </div>
          )}
          {loading && (
            <span style={s.loadingHint}>
              <span className="spinner" style={s.spinnerInline} /> Chargement…
            </span>
          )}
        </div>

        {error && <div style={s.errorBox}>{error}</div>}
        {reconcileError && tab === "banque" && (
          <div style={s.errorBox}>{reconcileError}</div>
        )}
        {geminiError && tab === "gemini" && (
          <div style={s.errorBox}>{geminiError}</div>
        )}

        {/* Rapport de rapprochement */}
        {data && tab === "banque" && (
          <div style={s.reconcileBox}>
            <div style={s.reconcileLabel}>📊 Rapport de rapprochement</div>
            <div style={s.reportGrid}>
              <div style={s.reportStat}>
                <div style={s.reportStatValue}>{report.total}</div>
                <div style={s.reportStatLabel}>Versements analysés</div>
              </div>
              <div style={s.reportStat}>
                <div style={{ ...s.reportStatValue, color: "#22c55e" }}>
                  {report.confirmed}
                </div>
                <div style={s.reportStatLabel}>Confirmés</div>
              </div>
              <div style={s.reportStat}>
                <div style={{ ...s.reportStatValue, color: "#ef4444" }}>
                  {report.notFound}
                </div>
                <div style={s.reportStatLabel}>Introuvables</div>
              </div>
              <div style={s.reportStat}>
                <div style={{ ...s.reportStatValue, color: "#64748b" }}>
                  {report.pending}
                </div>
                <div style={s.reportStatLabel}>En attente de relevé</div>
              </div>
              <div style={s.reportStat}>
                <div style={{ ...s.reportStatValue, color: ORPHAN_STATUS.color }}>
                  {report.unmatchedBankCount}
                </div>
                <div style={s.reportStatLabel}>Banque sans versement</div>
              </div>
            </div>
            <div style={s.reportTotalsRow}>
              <span>Total pompiste : <strong>{fmtNum(report.sumPompiste)}</strong></span>
              <span>Total banque (confirmés) : <strong>{fmtNum(report.sumBanque)}</strong></span>
              <span style={{ color: report.difference === 0 ? "#22c55e" : "#f59e0b" }}>
                Écart : <strong>{fmtNum(report.difference)}</strong>
              </span>
              {report.unmatchedBankCount > 0 && (
                <span style={{ color: ORPHAN_STATUS.color }}>
                  Banque sans versement : <strong>{fmtNum(report.unmatchedBankSum)}</strong>
                </span>
              )}
            </div>
            <div style={s.reportUploadNote}>
              {report.lastUploadedAt
                ? `Dernier relevé chargé le ${fmtDateTime(report.lastUploadedAt)}`
                : "Aucun relevé bancaire chargé pour cette sélection"}
            </div>
          </div>
        )}

        {/* VERSEMENT section */}
        {data && tab === "banque" && (
          <>
            <div style={s.sectionTitle}>💵 VERSEMENTS</div>
            <div className="table-scroll">
                <table style={s.table}>
                  <thead>
                    <tr>
                      <th style={s.th}>Date pompiste</th>
                      <th style={{ ...s.th, textAlign: "right" }}>
                        Montant pompiste
                      </th>
                      <th style={s.th}>Date banque</th>
                      <th style={{ ...s.th, textAlign: "right" }}>
                        Montant banque
                      </th>
                      <th style={s.th}>Libellé de l'opération</th>
                      <th style={s.th}>Comparaison</th>
                      <th style={s.th}>Déjà vérifié</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableRows.map((row) => {
                      if (row.type === "empty") {
                        return (
                          <tr key={row.key} style={s.tr}>
                            <td style={{ ...s.td, color: "#64748b" }}>
                              {displayDate(row.dateVers)}
                            </td>
                            <td style={{ ...s.td, textAlign: "right", color: "#475569" }}>—</td>
                            <td style={{ ...s.td, color: "#475569" }}>—</td>
                            <td style={{ ...s.td, textAlign: "right", color: "#475569" }}>—</td>
                            <td style={{ ...s.td, color: "#475569" }}>—</td>
                            <td style={{ ...s.td, color: EMPTY_DAY_STATUS.color, fontWeight: 600 }}>
                              {EMPTY_DAY_STATUS.label}
                            </td>
                            <td style={{ ...s.td, color: "#475569" }}>—</td>
                          </tr>
                        );
                      }

                      if (row.type === "orphan") {
                        const o = row.o;
                        return (
                          <tr key={row.key} style={s.tr}>
                            <td style={{ ...s.td, color: "#475569" }}>—</td>
                            <td style={{ ...s.td, textAlign: "right", color: "#475569" }}>—</td>
                            <td style={{ ...s.td, color: "#e2e8f0" }}>{o.date}</td>
                            <td style={{ ...s.td, textAlign: "right", fontWeight: 600 }}>
                              {fmtNum(o.montant)}
                            </td>
                            <td style={{ ...s.td, color: "#e2e8f0" }}>{o.libelle || "—"}</td>
                            <td style={{ ...s.td, color: ORPHAN_STATUS.color, fontWeight: 600 }}>
                              {ORPHAN_STATUS.label}
                            </td>
                            <td style={{ ...s.td, color: "#475569" }}>—</td>
                          </tr>
                        );
                      }

                      const v = row.v;
                      const status = versStatus(v);
                      return (
                        <tr
                          key={row.key}
                          style={{ ...s.tr, ...s.trClickable }}
                          onClick={() => handleRowClick(v)}
                          title="Voir la photo BV"
                        >
                          <td style={{ ...s.td, color: "#64748b" }}>
                            {displayDate(v.dateVers)}
                          </td>
                          <td
                            style={{
                              ...s.td,
                              textAlign: "right",
                              fontWeight: 600,
                            }}
                          >
                            {fmtNum(v.montant)}
                          </td>
                          <td style={{ ...s.td, color: v.bankDate ? "#e2e8f0" : "#475569" }}>
                            {v.bankDate || "—"}
                          </td>
                          <td
                            style={{
                              ...s.td,
                              textAlign: "right",
                              fontWeight: 600,
                            }}
                          >
                            {v.bankMontant != null ? fmtNum(v.bankMontant) : "—"}
                          </td>
                          <td style={{ ...s.td, color: v.bankLibelle ? "#e2e8f0" : "#475569" }}>
                            {v.bankLibelle || "—"}
                          </td>
                          <td style={{ ...s.td, color: status.color, fontWeight: 600 }}>
                            {status.label}
                          </td>
                          <td style={s.td} onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={!!v.verified}
                              onChange={() => {
                                const action = v.verified
                                  ? "retirer la vérification de"
                                  : "confirmer";
                                if (
                                  window.confirm(
                                    `Voulez-vous ${action} le versement ${v.item} du ${displayDate(v.dateVers)} ?`,
                                  )
                                ) {
                                  handleToggleVerified(v);
                                }
                              }}
                              style={s.checkbox}
                              title="Marquer comme déjà vérifié"
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr style={s.tfootRow}>
                      <td style={{ ...s.td, fontWeight: 700 }}>Total</td>
                      <td style={{ ...s.td, textAlign: "right", fontWeight: 700 }}>
                        {fmtNum(totalMontantPompiste)}
                      </td>
                      <td style={s.td} />
                      <td style={{ ...s.td, textAlign: "right", fontWeight: 700 }}>
                        {fmtNum(totalMontantBanque)}
                      </td>
                      <td style={s.td} />
                      <td style={s.td} />
                      <td style={s.td} />
                    </tr>
                  </tfoot>
                </table>
            </div>
          </>
        )}

        {/* Onglet Gemini : même relevé, rapprochement fait par Gemini */}
        {data && tab === "gemini" && (
          <>
            <div style={s.reconcileBox}>
              <div style={s.reconcileLabel}>🤖 Rapprochement par Gemini</div>
              <div style={s.reportGrid}>
                <div style={s.reportStat}>
                  <div style={s.reportStatValue}>{geminiReport.total}</div>
                  <div style={s.reportStatLabel}>Versements analysés</div>
                </div>
                <div style={s.reportStat}>
                  <div style={{ ...s.reportStatValue, color: "#22c55e" }}>
                    {geminiReport.conforme}
                  </div>
                  <div style={s.reportStatLabel}>Confirmés</div>
                </div>
                <div style={s.reportStat}>
                  <div style={{ ...s.reportStatValue, color: "#eab308" }}>
                    {geminiReport.probable}
                  </div>
                  <div style={s.reportStatLabel}>Probables</div>
                </div>
                <div style={s.reportStat}>
                  <div style={{ ...s.reportStatValue, color: "#f59e0b" }}>
                    {geminiReport.ecart}
                  </div>
                  <div style={s.reportStatLabel}>Écarts de montant</div>
                </div>
                <div style={s.reportStat}>
                  <div style={{ ...s.reportStatValue, color: "#ef4444" }}>
                    {geminiReport.notFound}
                  </div>
                  <div style={s.reportStatLabel}>Introuvables</div>
                </div>
                <div style={s.reportStat}>
                  <div style={{ ...s.reportStatValue, color: "#64748b" }}>
                    {geminiReport.pending}
                  </div>
                  <div style={s.reportStatLabel}>En attente de relevé</div>
                </div>
                <div style={s.reportStat}>
                  <div style={{ ...s.reportStatValue, color: ORPHAN_STATUS.color }}>
                    {geminiReport.unmatchedBankCount}
                  </div>
                  <div style={s.reportStatLabel}>Banque sans versement</div>
                </div>
              </div>
              <div style={s.reportTotalsRow}>
                <span>Total pompiste : <strong>{fmtNum(totalMontantPompiste)}</strong></span>
                <span>
                  Total banque (Gemini) : <strong>{fmtNum(geminiReport.sumBanque)}</strong>
                </span>
                <span style={{ color: geminiReport.difference === 0 ? "#22c55e" : "#f59e0b" }}>
                  Écart : <strong>{fmtNum(geminiReport.difference)}</strong>
                </span>
                {geminiReport.unmatchedBankCount > 0 && (
                  <span style={{ color: ORPHAN_STATUS.color }}>
                    Banque sans versement :{" "}
                    <strong>{fmtNum(geminiReport.unmatchedBankSum)}</strong>
                  </span>
                )}
              </div>
              <div style={s.reportUploadNote}>
                {geminiReport.lastAt
                  ? `Dernière analyse Gemini le ${fmtDateTime(geminiReport.lastAt)}`
                  : "Aucun relevé analysé par Gemini pour cette sélection"}
              </div>
            </div>

            <div style={s.sectionTitle}>💵 VERSEMENTS</div>
            <div className="table-scroll">
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>Date pompiste</th>
                    <th style={{ ...s.th, textAlign: "right" }}>Montant pompiste</th>
                    <th style={s.th}>Date banque</th>
                    <th style={{ ...s.th, textAlign: "right" }}>Montant banque</th>
                    <th style={s.th}>Libellé de l'opération</th>
                    <th style={s.th}>Comparaison</th>
                    <th style={s.th}>Remarque Gemini</th>
                  </tr>
                </thead>
                <tbody>
                  {geminiTableRows.map((row) => {
                    if (row.type === "empty") {
                      return (
                        <tr key={row.key} style={s.tr}>
                          <td style={{ ...s.td, color: "#64748b" }}>
                            {displayDate(row.dateVers)}
                          </td>
                          <td style={{ ...s.td, textAlign: "right", color: "#475569" }}>—</td>
                          <td style={{ ...s.td, color: "#475569" }}>—</td>
                          <td style={{ ...s.td, textAlign: "right", color: "#475569" }}>—</td>
                          <td style={{ ...s.td, color: "#475569" }}>—</td>
                          <td style={{ ...s.td, color: EMPTY_DAY_STATUS.color, fontWeight: 600 }}>
                            {EMPTY_DAY_STATUS.label}
                          </td>
                          <td style={{ ...s.td, color: "#475569" }}>—</td>
                        </tr>
                      );
                    }

                    if (row.type === "orphan") {
                      const o = row.o;
                      return (
                        <tr key={row.key} style={s.tr}>
                          <td style={{ ...s.td, color: "#475569" }}>—</td>
                          <td style={{ ...s.td, textAlign: "right", color: "#475569" }}>—</td>
                          <td style={{ ...s.td, color: "#e2e8f0" }}>{o.date}</td>
                          <td style={{ ...s.td, textAlign: "right", fontWeight: 600 }}>
                            {fmtNum(o.montant)}
                          </td>
                          <td style={{ ...s.td, color: "#e2e8f0" }}>{o.libelle || "—"}</td>
                          <td style={{ ...s.td, color: ORPHAN_STATUS.color, fontWeight: 600 }}>
                            {ORPHAN_STATUS.label}
                          </td>
                          <td style={{ ...s.td, color: "#475569" }}>—</td>
                        </tr>
                      );
                    }

                    const v = row.v;
                    const g = v.geminiBank;
                    const status = geminiBankStatus(v);
                    return (
                      <tr
                        key={row.key}
                        style={{ ...s.tr, ...s.trClickable }}
                        onClick={() => handleRowClick(v)}
                        title="Voir la photo BV"
                      >
                        <td style={{ ...s.td, color: "#64748b" }}>
                          {displayDate(v.dateVers)}
                        </td>
                        <td style={{ ...s.td, textAlign: "right", fontWeight: 600 }}>
                          {fmtNum(v.montant)}
                        </td>
                        <td style={{ ...s.td, color: g?.bankDate ? "#e2e8f0" : "#475569" }}>
                          {g?.bankDate || "—"}
                        </td>
                        <td style={{ ...s.td, textAlign: "right", fontWeight: 600 }}>
                          {g?.bankMontant != null ? fmtNum(g.bankMontant) : "—"}
                          {g?.lignes?.length > 1 && (
                            <div style={s.comboDetail}>
                              {g.lignes.map((l) => fmtNum(l.montant)).join(" + ")}
                            </div>
                          )}
                        </td>
                        <td style={{ ...s.td, color: g?.bankLibelle ? "#e2e8f0" : "#475569" }}>
                          {g?.bankLibelle || "—"}
                        </td>
                        <td style={{ ...s.td, color: status.color, fontWeight: 600 }}>
                          {status.label}
                        </td>
                        <td
                          style={{
                            ...s.td,
                            color: g?.commentaire ? "#94a3b8" : "#475569",
                            fontSize: 12,
                          }}
                        >
                          {g?.commentaire || "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={s.tfootRow}>
                    <td style={{ ...s.td, fontWeight: 700 }}>Total</td>
                    <td style={{ ...s.td, textAlign: "right", fontWeight: 700 }}>
                      {fmtNum(totalMontantPompiste)}
                    </td>
                    <td style={s.td} />
                    <td style={{ ...s.td, textAlign: "right", fontWeight: 700 }}>
                      {fmtNum(geminiReport.sumBanque)}
                    </td>
                    <td style={s.td} />
                    <td style={s.td} />
                    <td style={s.td} />
                  </tr>
                </tfoot>
              </table>
            </div>
          </>
        )}

        {!data && !loading && (
          <div style={s.empty}>Sélectionnez une station et un mois.</div>
        )}
      </div>

      {photoModal && (
        <div style={s.modalOverlay} onClick={() => setPhotoModal(null)}>
          <div style={s.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <span>
                📷 BV — {photoModal.item} du {displayDate(photoModal.dateVers)}
              </span>
              <button style={s.modalClose} onClick={() => setPhotoModal(null)}>
                ✕
              </button>
            </div>
            {photoModal.loading && (
              <div style={s.empty}>
                <span className="spinner" style={s.spinnerInline} /> Chargement…
              </div>
            )}
            {!photoModal.loading && photoModal.error && (
              <div style={s.empty}>{photoModal.error}</div>
            )}
            {!photoModal.loading && photoModal.url && (
              <img src={photoModal.url} alt="BV" style={s.modalImage} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  root: {
    minHeight: "100vh",
    background: "#0f172a",
    padding: 24,
  },
  card: {
    background: "#1e293b",
    borderRadius: 16,
    padding: "32px 28px",
    maxWidth: 900,
    margin: "0 auto",
    boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    marginBottom: 24,
  },
  backBtn: {
    background: "transparent",
    border: "1px solid #334155",
    borderRadius: 8,
    color: "#94a3b8",
    padding: "8px 14px",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  title: {
    fontSize: 18,
    fontWeight: 800,
    color: "#e2e8f0",
    letterSpacing: 1,
  },
  subtitle: {
    fontSize: 12,
    color: "#64748b",
    marginTop: 3,
    letterSpacing: 0.5,
    textTransform: "uppercase",
  },
  tabs: {
    display: "flex",
    gap: 8,
    marginBottom: 16,
    borderBottom: "1px solid #334155",
  },
  tab: {
    background: "transparent",
    border: "none",
    borderBottom: "2px solid transparent",
    color: "#64748b",
    padding: "8px 14px",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: 0.5,
    marginBottom: -1,
  },
  tabActive: {
    color: "#0ea5e9",
    borderBottom: "2px solid #0ea5e9",
  },
  selRow: {
    display: "flex",
    gap: 12,
    alignItems: "flex-end",
    flexWrap: "wrap",
    marginBottom: 24,
    padding: "16px",
    background: "#0f172a",
    borderRadius: 10,
  },
  selGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  label: {
    fontSize: 11,
    color: "#64748b",
    fontWeight: 600,
    letterSpacing: 0.5,
    textTransform: "uppercase",
  },
  select: {
    background: "#1e293b",
    border: "1px solid #334155",
    borderRadius: 8,
    color: "#e2e8f0",
    padding: "8px 12px",
    fontSize: 13,
    cursor: "pointer",
    minWidth: 140,
  },
  dateInput: {
    background: "#1e293b",
    border: "1px solid #334155",
    borderRadius: 8,
    color: "#e2e8f0",
    padding: "8px 12px",
    fontSize: 13,
  },
  loadingHint: {
    fontSize: 13,
    color: "#64748b",
    fontWeight: 600,
  },
  spinnerInline: {
    marginRight: 6,
  },
  errorBox: {
    background: "#7f1d1d20",
    border: "1px solid #ef444440",
    borderRadius: 8,
    color: "#ef4444",
    padding: "10px 14px",
    marginBottom: 16,
    fontSize: 13,
  },
  reconcileBox: {
    background: "#0f172a",
    border: "1px dashed #334155",
    borderRadius: 10,
    padding: "16px",
    marginBottom: 24,
  },
  reconcileLabel: {
    fontSize: 12,
    fontWeight: 700,
    color: "#0ea5e9",
    letterSpacing: 0.5,
    textTransform: "uppercase",
    marginBottom: 12,
  },
  fileBtn: {
    background: "#0ea5e9",
    border: "none",
    borderRadius: 8,
    color: "white",
    padding: "8px 16px",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
    letterSpacing: 0.5,
    display: "inline-block",
  },
  reportGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
    gap: 12,
    marginBottom: 14,
  },
  reportStat: {
    background: "#1e293b",
    border: "1px solid #334155",
    borderRadius: 8,
    padding: "12px 14px",
    textAlign: "center",
  },
  reportStatValue: {
    fontSize: 22,
    fontWeight: 800,
    color: "#e2e8f0",
  },
  reportStatLabel: {
    fontSize: 11,
    color: "#64748b",
    marginTop: 4,
    letterSpacing: 0.3,
    textTransform: "uppercase",
  },
  reportTotalsRow: {
    display: "flex",
    gap: 24,
    flexWrap: "wrap",
    fontSize: 13,
    color: "#94a3b8",
  },
  reportUploadNote: {
    marginTop: 10,
    paddingTop: 10,
    borderTop: "1px solid #1e3a5f",
    fontSize: 12,
    color: "#64748b",
    fontStyle: "italic",
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 700,
    color: "#0ea5e9",
    letterSpacing: 1.5,
    textTransform: "uppercase",
    marginBottom: 14,
    paddingBottom: 6,
    borderBottom: "1px solid #1e3a5f",
  },
  empty: {
    textAlign: "center",
    color: "#475569",
    padding: "24px 0",
    fontSize: 13,
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 13,
  },
  th: {
    background: "#0f172a",
    color: "#64748b",
    fontWeight: 700,
    fontSize: 11,
    letterSpacing: 0.5,
    textTransform: "uppercase",
    padding: "8px 12px",
    textAlign: "left",
  },
  tr: {
    borderBottom: "1px solid #1e293b",
  },
  trClickable: {
    cursor: "pointer",
  },
  checkbox: {
    width: 16,
    height: 16,
    cursor: "pointer",
  },
  modalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.7)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    zIndex: 1000,
  },
  modalContent: {
    background: "#1e293b",
    borderRadius: 12,
    padding: 20,
    maxWidth: "90vw",
    maxHeight: "90vh",
    overflow: "auto",
    boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
  },
  modalHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    marginBottom: 14,
    color: "#e2e8f0",
    fontWeight: 700,
    fontSize: 14,
  },
  modalClose: {
    background: "transparent",
    border: "1px solid #334155",
    borderRadius: 6,
    color: "#94a3b8",
    padding: "4px 10px",
    cursor: "pointer",
    fontSize: 13,
  },
  modalImage: {
    maxWidth: "100%",
    maxHeight: "75vh",
    borderRadius: 8,
    display: "block",
  },
  comboDetail: {
    fontSize: 11,
    fontWeight: 400,
    color: "#94a3b8",
    marginTop: 2,
    whiteSpace: "nowrap",
  },
  tfootRow: {
    borderTop: "2px solid #334155",
  },
  td: {
    padding: "10px 12px",
    color: "#e2e8f0",
  },
};
